/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";
import Swal from "sweetalert2";
import QuestionInput from "./QuestionInput";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";

const FormQuestions = ({ formCode, isAgreed, formPrice, formFields, onAnswerFeedback }) => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const currentLang = lang || 'en';
  const [session, setSession] = useState(null);
  const [progress, setProgress] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answer, setAnswer] = useState("");
  const [dependentAnswers, setDependentAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [answersHistory, setAnswersHistory] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(1);

  const [, setIsGuest] = useState(false);
  const [isSummaryView, setIsSummaryView] = useState(false);
  const [summaryAnswers, setSummaryAnswers] = useState([]);

  const STORAGE_KEY = `form_session_${formCode}`;
  const DEVICE_ID_KEY = "device_id";

  // === Helper Functions ===
  const getDeviceId = () => {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = `guest_${Math.random().toString(36).substr(2, 12)}_${Date.now()}`;
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  };

  const getAuthToken = () => {
    const raw = localStorage.getItem("auth_token");
    if (!raw) return null;
    if (raw.includes("|") && raw.includes("auth_user")) {
      return raw.split("|")[1].split("auth_user")[0];
    }
    return raw.trim();
  };

  const saveToStorage = useCallback(() => {
    if (session) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          session,
          progress,
          answersHistory,
          timestamp: Date.now(),
        })
      );
    }
  }, [session, progress, answersHistory, STORAGE_KEY]);

  const loadFromStorage = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const data = JSON.parse(saved);
      if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
        return data;
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    return null;
  };

  // === Utility: Check if value is empty ===
  const isEmpty = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "boolean") return false;
    return !value;
  };

  // === Utility: Check if value has valid input ===
  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "boolean") return value === true;
    return !!value;
  };

  // === Custom Validation Message ===
  const generateCustomValidationMessage = (question, value) => {
    if (!question) return null;

    const { label, type, validation = [], required } = question;

    const fullField = formFields?.find(f => f.id === question.id || f.code === question.code);
    const apiMessages = fullField?.validation_messages || question.validation_messages;

    const validation_messages = (apiMessages &&
      typeof apiMessages === 'object' &&
      !Array.isArray(apiMessages))
      ? apiMessages
      : {};

    const fieldName = label ? `"${label}"` : "This field";

    const rules = Array.isArray(validation)
      ? validation
      : typeof validation === "string"
        ? [validation]
        : [];

    const getMsg = (ruleKey, fallback) => {
      if (validation_messages[ruleKey]) {
        return validation_messages[ruleKey];
      }
      return fallback;
    };

    if (required && isEmpty(value)) {
      return getMsg("required", `${fieldName} is required.`);
    }

    for (const rule of rules) {
      const ruleName = rule.split(":")[0];
      const ruleValue = rule.includes(":") ? rule.split(":")[1] : null;

      switch (ruleName) {
        case "required":
          if (isEmpty(value)) return getMsg("required", `${fieldName} is required.`);
          break;
        case "email":
          if (value && !/^\S+@\S+\.\S+$/.test(value)) return getMsg("email", `Please enter a valid email address.`);
          break;
        case "numeric":
        case "integer":
          if (value && isNaN(value)) return getMsg(ruleName, `${fieldName} must be a number.`);
          break;
        case "max":
          if (ruleValue) {
            const max = parseInt(ruleValue);
            if (typeof value === "string" && value.length > max) {
              return getMsg("max", `${fieldName} must be no more than ${max} characters.`);
            }
            if (typeof value === "number" && value > max) {
              return getMsg("max", `${fieldName} must not exceed ${max}.`);
            }
          }
          break;
        case "min":
          if (ruleValue) {
            const min = parseInt(ruleValue);
            if (typeof value === "string" && value.length < min) return getMsg("min", `${fieldName} must be at least ${min} characters.`);
            if (typeof value === "number" && value < min) return getMsg("min", `${fieldName} must be at least ${min}.`);
          }
          break;
        case "regex":
          if (ruleValue && value) {
            try {
              const regex = new RegExp(ruleValue);
              if (!regex.test(value)) return getMsg("regex", `The format of ${fieldName} is invalid.`);
            } catch (e) { }
          }
          break;
        case "in":
          if (ruleValue && value !== undefined && value !== null) {
            const allowed = ruleValue.split(",").map(v => v.trim());
            if (!allowed.includes(String(value))) return getMsg("in", `${fieldName} must be one of: ${allowed.join(", ")}.`);
          }
          break;
        default: break;
      }
    }
    return null;
  };

  // === Helper: Check Conditional Logic ===
  const isConditionMet = useCallback((depQ, mainValue) => {
    if (!depQ.conditional_logic) return true;
    const { conditions = [], logic_type = 'all' } = depQ.conditional_logic;
    
    // Normalize logic
    const results = conditions.map(c => {
       const op = c.operator;
       const val = String(c.value || '').trim().toLowerCase();
       const actual = String(mainValue || '').trim().toLowerCase();
       
       if (op === 'equals') return actual === val;
       if (op === 'not_equals') return actual !== val;
       if (op === 'contains') return actual.includes(val);
       return false;
    });

    return logic_type === 'any' ? results.some(r=>r) : results.every(r=>r);
  }, []);

  // === Start Session ===
  const startSession = async () => {
    setLoading(true);
    const token = getAuthToken();
    setIsGuest(!token);

    try {
      const saved = loadFromStorage();
      if (saved && saved.session?.response_id) {
        const headers = token
          ? { Authorization: `Bearer ${token}` }
          : { "X-Device-ID": getDeviceId() };

        const progressResponse = await api.get(
          `/form-answering/sessions/${saved.session.response_id}/progress`,
          { headers }
        );

        const freshProgress = progressResponse.data.data;

        setSession(saved.session);
        setProgress(freshProgress);
        setCurrentQuestion(freshProgress.next_question || saved.progress.next_question);
        setAnswersHistory(saved.answersHistory || []);

        // ضبط المؤشر المرئي بناءً على عدد الأسئلة المجابة + 1 للسؤال الحالي
        setCurrentQuestionIndex(freshProgress.answered > 0 ? freshProgress.answered + 1 : 1);

        Swal.fire({
          icon: "info",
          title: "Session Resumed",
          text: "Your previous progress has been restored.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        const headers = token
          ? { Authorization: `Bearer ${token}` }
          : { "X-Device-ID": getDeviceId() };
        const response = await api.post(
          `/form-answering/start/${formCode}`,
          {},
          { headers }
        );
        const { session, progress } = response.data.data;
        setSession(session);
        setProgress(progress);
        setCurrentQuestion(progress.next_question);
        setAnswersHistory([]);
        setCurrentQuestionIndex(1);
      }
    } catch (error) {
      Swal.fire("Error", "Failed to start session.", "error");
    } finally {
      setLoading(false);
    }

  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const headers = getAuthToken()
        ? { Authorization: `Bearer ${getAuthToken()}` }
        : { "X-Device-ID": getDeviceId() };
      
      const response = await api.get(
        `/form-answering/sessions/${session.response_id}/answers`,
        { headers }
      );
      
      setSummaryAnswers(response.data.data.answers || []);
      setIsSummaryView(true);
    } catch (error) {
      console.error("Fetch Summary Error:", error);
      Swal.fire("Error", "Failed to load summary.", "error");
    } finally {
      setLoading(false);
    }
  };

  const goToPrevious = async () => {
    setLoading(true);
    try {
      const headers = getAuthToken()
        ? { Authorization: `Bearer ${getAuthToken()}` }
        : { "X-Device-ID": getDeviceId() };

      const response = await api.get(
        `/form-answering/sessions/${session.response_id}/previous`,
        {
          headers,
          params: {
            current_question_id: currentQuestion.id
          }
        }
      );

      const { previous_question } = response.data.data;

      // Update current question and answer
      setCurrentQuestion(previous_question);
      setAnswer(previous_question.current_answer || "");

      // تنقيص المؤشر المرئي عند العودة للخلف
      setCurrentQuestionIndex(prev => Math.max(1, prev - 1));

      // ✅ DON'T modify progress - it stays accurate
      // Progress still shows 3/47 answered even when viewing question 1

    } catch (error) {
      if (error.response?.status === 422 || error.response?.data?.success === false) {
        Swal.fire({
          icon: "info",
          title: "First Question",
          text: error.response?.data?.message || "You are already at the beginning of the form.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        console.error("Previous Question Error:", error);
        Swal.fire("Error", "Failed to retrieve previous question.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // === Start Over Functionality ===
  const startOver = async () => {
    const result = await Swal.fire({
      title: "Start Over?",
      text: "This will reset all your answers and start from the beginning.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, restart",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;

    try {
      setLoading(true);

      // 1. مسح الحالة (State)
      setSession(null);
      setProgress(null);
      setCurrentQuestion(null);
      setAnswer("");
      setAnswersHistory([]);
      setCurrentQuestionIndex(1);

      // 2. مسح التخزين المحلي وتوليد معرف جهاز جديد (لإجبار الـ API على البدء من جديد)
      localStorage.removeItem(STORAGE_KEY);
      const token = getAuthToken();

      let headers = {};
      if (token) {
        headers = { Authorization: `Bearer ${token}` };
      } else {
        // إذا كان Guest، نولد ID جديد تماماً
        const newDeviceId = `guest_${Math.random().toString(36).substr(2, 12)}_${Date.now()}`;
        localStorage.setItem(DEVICE_ID_KEY, newDeviceId);
        headers = { "X-Device-ID": newDeviceId };
      }

      // 3. طلب جلسة جديدة الآن
      const response = await api.post(
        `/form-answering/start/${formCode}`,
        {},
        { headers }
      );

      const { session: newSession, progress: newProgress } = response.data.data;

      setSession(newSession);
      setProgress(newProgress);
      setCurrentQuestion(newProgress.next_question);

      Swal.fire({
        icon: "success",
        title: "Restarted!",
        text: "You are back at the first question.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire("Error", "Failed to restart the session.", "error");
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async (stayOnCurrent = false, overrideValue = null) => {
    let finalValue = overrideValue !== null ? overrideValue : answer;

    const localError = generateCustomValidationMessage(currentQuestion, finalValue);
    if (localError) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: localError,
        confirmButtonText: "OK",
      });
      onAnswerFeedback?.(false);
      return false;
    }

    if (currentQuestion.type === "date" && finalValue) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(finalValue)) {
        const [y, m, d] = finalValue.split("-");
        finalValue = `${m}/${d}/${y}`;
      }
    }

    if (currentQuestion.type === "checkbox_group") {
      if (!Array.isArray(finalValue)) {
        if (finalValue === "" || finalValue === null || finalValue === undefined) {
          finalValue = [];
        } else {
          finalValue = [finalValue];
        }
      }
    }

    // Identify and Validate Visible Dependents
    const visibleDependents = (currentQuestion.dependent_questions || []).filter(dq => isConditionMet(dq, finalValue));
    
    for (const dq of visibleDependents) {
        const dVal = dependentAnswers[dq.id];
        // Reuse validation logic for dependents
        const dError = generateCustomValidationMessage(dq, dVal);
        if (dError) {
             Swal.fire({
                 icon: "warning",
                 title: "Validation Error", 
                 text: `${dq.label || 'Field'}: ${dError}`, // Better context
                 confirmButtonText: "OK"
             });
             return false;
        }
    }

    setSubmitting(true);
    try {
      const headers = getAuthToken()
        ? { Authorization: `Bearer ${getAuthToken()}` }
        : { "X-Device-ID": getDeviceId() };

      const postAnswer = async (qId, val) => {
         return api.post(
            `/form-answering/sessions/${session.response_id}/answer`,
            { question_id: qId, value: val },
            { headers }
         );
      };

      // 1. Submit Main Question
      const response = await postAnswer(currentQuestion.id, finalValue);

      const { answer: savedAnswer } = response.data.data;
      let latestProgress = response.data.data.progress;
      const isValid = savedAnswer?.is_valid !== false;
      onAnswerFeedback?.(isValid);

      if (!stayOnCurrent) {
        setAnswersHistory((prev) => [
          ...prev,
          { question: currentQuestion, answer: finalValue },
        ]);
      }

      // 2. Submit Dependent Questions Sequentially
      for (const dq of visibleDependents) {
           const dVal = dependentAnswers[dq.id];
           
           // Ensure array handling for dependent checklist if needed?
           // Assuming Dependents are simple for now (Text/Select).
           
           const dRes = await postAnswer(dq.id, dVal);
           latestProgress = dRes.data.data.progress;
           
           if (!stayOnCurrent) {
               setAnswersHistory((prev) => [...prev, { question: dq, answer: dVal }]);
           }
      }

      setProgress(latestProgress);

      if (!stayOnCurrent) {
        setCurrentQuestion(latestProgress.next_question);
        setAnswer("");
        setDependentAnswers({});
        // Increment visual index by 1 (Main) + count of dependents
        setCurrentQuestionIndex(prev => prev + 1 + visibleDependents.length);
      }

      if (latestProgress.is_complete) {
        await fetchSummary();
      }

      return true;
    } catch (error) {
      let title = "Input Error";
      let text = "Please check your answer.";

      if (error.response?.status === 422) {
        const res = error.response.data;

        if (res.error && typeof res.error === "string") {
          text = res.error;
        } else if (res.errors?.value?.[0]) {
          text = res.errors.value[0];
        } else {
          text = generateCustomValidationMessage(currentQuestion, answer);
        }
      } else {
        text = error.response?.data?.message || "Failed to save answer.";
      }

      Swal.fire({
        icon: "error",
        title,
        text,
        confirmButtonText: "OK",
      });

      onAnswerFeedback?.(false);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // === Form Completion ===
  const handleFormCompletion = async () => {
    try {
      const headers = getAuthToken()
        ? { Authorization: `Bearer ${getAuthToken()}` }
        : { "X-Device-ID": getDeviceId() };
      await api.post(
        `/form-answering/sessions/${session.response_id}/complete`,
        {},
        { headers }
      );

      const checkoutRes = await api.post(
        `/payments/create-checkout/${session.response_id}`,
        {},
        { headers }
      );
      const data = checkoutRes.data;

      localStorage.removeItem(STORAGE_KEY);

      if (
        !getAuthToken() &&
        (data.message.includes("free") || !data.data?.checkout_url)
      ) {
        showAuthPrompt();
        return;
      }

      if (data.message.includes("free") || !data.data?.checkout_url) {
        await Swal.fire({
          icon: "success",
          title: "Form Submitted!",
          text: "Thank you!",
          confirmButtonText: "View Profile",
        });
        const getRoleFromSession = localStorage.getItem("auth_role");
        navigate(`/${currentLang}/profile/${getRoleFromSession}`);
      } else {
        const checkoutUrl = data.data.checkout_url;
        if (checkoutUrl) {
          if (!getAuthToken()) {
            localStorage.setItem("pending_checkout_url", checkoutUrl);
            showAuthPrompt();
          } else {
            window.location.href = checkoutUrl;
          }
        }
      }
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to complete form.";
      Swal.fire("Error", msg, "error");
    }
  };

  const showAuthPrompt = () => {
    Swal.fire({
      icon: "info",
      title: "Almost there!",
      html: `
        <p class="text-sm text-gray-600 mb-4">To access the completed form, please log in or create an account.</p>
        <div class="flex gap-3 justify-center mt-6">
          <button id="login-btn" class="px-5 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">Log In</button>
          <button id="signup-btn" class="px-5 py-2 border border-blue-600 text-blue-600 rounded-lg font-medium hover:bg-blue-50">Sign Up</button>
        </div>
      `,
      showConfirmButton: false,
      allowOutsideClick: false,
      didOpen: () => {
        document.getElementById("login-btn").addEventListener("click", () => {
          Swal.close();
          navigate(`/${currentLang}/login`);
        });
        document.getElementById("signup-btn").addEventListener("click", () => {
          Swal.close();
          navigate(`/${currentLang}/register`);
        });
      },
    });
  };

  // === useEffects ===
  useEffect(() => {
    if (isAgreed && !session) {
      startSession();
    }
  }, [isAgreed, formCode]);

  useEffect(() => {
    // تعديل هام: لا تمسح البيانات عند الريفريش (عندما تكون isAgreed كاذبة والجلسة غير محملة أصلاً)
    // فقط امسحها إذا كان هناك جلسة نشطة والمستخدم قام بإلغاء الموافقة يدوياً
    if (!isAgreed && session) {
      setSession(null);
      setProgress(null);
      setCurrentQuestion(null);
      setAnswer("");
      setAnswersHistory([]);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isAgreed, session, STORAGE_KEY]);

  useEffect(() => {
    saveToStorage();
  }, [saveToStorage]);

  // === UI Rendering ===
  if (!isAgreed)
    return (
      <div className="mt-6 p-6 bg-gray-100 rounded-lg text-center">
        <p className="text-gray-600">Please agree to start filling.</p>
      </div>
    );

  if (loading)
    return (
      <div className="mt-6 p-6 bg-blue-50 rounded-lg text-center">
        <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 rounded-full inline-block"></div>
        <p className="mt-2">Starting session...</p>
      </div>
    );

  if (isSummaryView) {
    return (
      <SummaryView 
        answers={summaryAnswers} 
        onSubmit={handleFormCompletion} 
        onBack={() => setIsSummaryView(false)} 
        submitting={submitting}
      />
    );
  }

  if (!currentQuestion || !currentQuestion.id) {
    return (
      <div className="mt-8 p-6 bg-white rounded-xl shadow-md border border-gray-200 text-center">
        <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 rounded-full inline-block"></div>
        <p className="mt-2 text-gray-600">Loading question...</p>
      </div>
    );
  }




  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="mt-8 p-6 bg-white rounded-xl shadow-md border border-gray-200"
    >

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center text-sm text-gray-600 mb-3">
          <span className="font-medium">
            Question {currentQuestionIndex} of {progress.total_questions}
          </span>
          <span className="font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full text-sm">
            {Math.round(progress.percentage)}% Complete
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <motion.div
            className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress.percentage}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
      </div>

      <div className="mb-8">
        <QuestionInput
          question={currentQuestion}
          value={answer}
          onChange={(newValue) => {
            setAnswer(newValue);

            // Clear dependents if main answer changes significantly?
            // Actually, we should keep them but maybe they become invalid/hidden.
            // Logic filters visibility anyway.

            if (
              currentQuestion.type === "signature" &&
              newValue &&
              !submitting
            ) {
              setTimeout(() => submitAnswer(false, newValue), 100);
            }
          }}
          disabled={submitting}
          dependentQuestions={currentQuestion.dependent_questions}
          dependentAnswers={dependentAnswers}
          onDependentChange={(id, val) => setDependentAnswers(prev => ({...prev, [id]: val}))}
          isConditionMet={(dq) => isConditionMet(dq, answer)}
        />
      </div>

      <div className="f-col flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50 p-4 rounded-lg">
        <div className="w-100 f-col flex gap-2">
          <motion.button
            onClick={goToPrevious}
            disabled={submitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`
              w-100 flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200
              ${!submitting
                ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg hover:shadow-xl"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }
            `}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Previous</span>
          </motion.button>

          <motion.button
            onClick={startOver}
            disabled={submitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-2 px-6 py-3 bg-red-100 text-red-600 hover:bg-red-200 rounded-lg font-semibold text-sm transition-all duration-200"
            title="Start from beginning"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h5M20 20v-5h-5" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12a9 9 0 1018 0 9 9 0 00-18 0z" />
            </svg>
            <span className="">Start Over</span>
          </motion.button>
        </div>

        <div className="flex flex-wrap gap-3 justify-end w-full sm:w-auto">
          <button
            onClick={() => setAnswer("")}
            className="w-100 px-6 py-3 text-sm font-medium text-gray-600 hover:text-gray-800 bg-white border-2 border-gray-200 rounded-lg hover:border-gray-300 transition-all duration-200"
            disabled={submitting}
          >
            Clear
          </button>

          <motion.button
            onClick={() => submitAnswer()}
            disabled={submitting || (currentQuestion.required && !hasValue(answer))}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="w-100 group flex items-center gap-3 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold text-sm shadow-lg hover:shadow-xl disabled:from-blue-400 disabled:to-blue-500 disabled:cursor-not-allowed disabled:shadow-none transition-all duration-200"
          >
            {submitting ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Saving...</span>
              </>
            ) : progress.is_complete ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Finish Form
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                Next Question
              </>
            )}
          </motion.button>
        </div>
      </div>

      {currentQuestion.depends_on && (
        <p className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          <span className="font-medium">Note:</span> This question depends on a previous answer.
        </p>
      )}
    </motion.div>
  );
};

const SummaryView = ({ answers, onSubmit, onBack, submitting }) => {
  return (
    <div className="mt-8 p-6 bg-white rounded-xl shadow-md border border-gray-200">
      <h2 className="text-xl font-bold text-gray-900 mb-6 border-b pb-4">Review Your Answers</h2>
      
      <div className="space-y-4 mb-8">
        {answers.map((item, index) => (
          <div key={index} className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 p-3 bg-gray-50 rounded-lg">
            <div className="font-medium text-gray-700 sm:w-1/3">{item.question_label || `Question ${index + 1}`}</div>
            <div className="text-gray-900 sm:w-2/3 font-semibold break-words">
              {Array.isArray(item.value) 
                ? item.value.join(", ") 
                : (item.value?.toString() || "-")}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
        <button
          onClick={onBack}
          disabled={submitting}
          className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
        >
          Back to Edit
        </button>
        
        <button
          onClick={onSubmit}
          disabled={submitting}
          className="px-8 py-2.5 bg-green-600 text-white rounded-lg font-bold shadow-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {submitting ? (
            <>
               <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
               <span>Submitting...</span>
            </>
          ) : (
            <>
              <span>Submit Form</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FormQuestions;